import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, ViewChild, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, catchError, Observable, of, Subscription, timeout } from 'rxjs';
import { Feedback } from '../models/feedback.model';
import {
  AiFeatureAvailability,
  AiFeatureGateService,
  GemConfirmationDialogComponent,
  GemDialogComponent,
  GemErrorDialogComponent,
  TokenHelperService,
} from 'gematik-form-library';
import { GemFeedbackChatbotDialogComponent } from './gem-feedback-chatbot-dialog.component';
import {
  GEM_FEEDBACK_CHATBOT_LAUNCHER,
  GemFeedbackChatbotLauncher,
} from '../services/gem-feedback-chatbot-launcher.token';
import { ChatContainerComponent, ChatService } from 'gematik-support-chatbot';
import { SteService } from '../services/ste.service';

const AI_PRECHECK_CATEGORIES = ['INQUIRY', 'INCIDENT'];

export interface GemFeedbackData {
  backendUrl: string;
  application: string;
  component: string;
}

type Step = 'home' | 'general' | 'inquiry' | 'chatbot';

@Component({
  selector: 'gem-feedback',
  styleUrls: ['gem-feedback.component.scss'],
  template: `
    <!-- Blocks the whole dialog and prevents closing while a request is in flight -->
    <div *ngIf="isLoading" class="gem-feedback-loading-overlay">
      <mat-spinner diameter="48"></mat-spinner>
      <p>{{ 'feedback.loadingText' | translate }}</p>
    </div>

    <!-- Home view: chatbot + 2 buttons -->
    <ng-container *ngIf="(currentStep$ | async) === 'home'">
      <div class="position-relative p-3 mt-3">
        <h3 class="text-center mb-1" style="color: var(--gematik-blue)">
          <strong>{{ 'feedback.homeTitle' | translate }}</strong>
        </h3>
        <p class="text-center text-muted mb-0">{{ 'feedback.homeSubTitle' | translate }}</p>
        <button mat-icon-button mat-dialog-close class="position-absolute end-0 m-2" style="top: 0px" tabindex="-1">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div mat-dialog-content class="mat-dialog-content" style="color: initial">
        <div
          *ngIf="canAccessChatbot"
          class="chatbot-section mb-5"
          [class.chatbot-section-disabled]="!chatInteractive"
          style="background-color: var(--gematik-light-grey)"
        >
          <div class="chatbot-header">
            <p class="chatbot-title" [innerHTML]="'feedback.chatbotLabel' | translate"></p>
          </div>
          <p class="chatbot-maintenance-hint" *ngIf="chatAvailability === 'MAINTENANCE'">
            {{ 'feedback.chatbotMaintenanceHint' | translate: { until: formatUntil(chatDisabledUntil) } }}
          </p>
          <div class="chatbot-bubbles">
            <span
              class="bubble-text bubble-pos-0 mt-2"
              *ngIf="chatbotSuggestions[0]"
              (click)="openChatbotWithQuestion(chatbotSuggestions[0])"
              [title]="'feedback.chatbotAskThis' | translate"
              >{{ chatbotSuggestions[0] }}</span
            >
            <span
              class="bubble-text bubble-pos-1"
              *ngIf="chatbotSuggestions[1]"
              (click)="openChatbotWithQuestion(chatbotSuggestions[1])"
              [title]="'feedback.chatbotAskThis' | translate"
              >{{ chatbotSuggestions[1] }}</span
            >
            <span
              class="bubble-text bubble-pos-2"
              *ngIf="chatbotSuggestions[2]"
              (click)="openChatbotWithQuestion(chatbotSuggestions[2])"
              [title]="'feedback.chatbotAskThis' | translate"
              >{{ chatbotSuggestions[2] }}</span
            >
            <span
              class="bubble-text bubble-pos-3"
              *ngIf="chatbotSuggestions[3]"
              (click)="openChatbotWithQuestion(chatbotSuggestions[3])"
              [title]="'feedback.chatbotAskThis' | translate"
              >{{ chatbotSuggestions[3] }}</span
            >
            <span
              class="bubble-text bubble-pos-4"
              *ngIf="chatbotSuggestions[4]"
              (click)="openChatbotWithQuestion(chatbotSuggestions[4])"
              [title]="'feedback.chatbotAskThis' | translate"
              >{{ chatbotSuggestions[4] }}</span
            >
          </div>
          <div class="chatbot-ask-btn-wrapper">
            <button
              type="button"
              class="btn btn-primary"
              [disabled]="!chatInteractive"
              (click)="changeStep('chatbot')"
              tabindex="-1"
              [ngbTooltip]="'feedback.chatbotInputTooltip' | translate"
            >
              {{ 'feedback.chatbotInputPlaceholder' | translate }}
            </button>
          </div>
        </div>

        <div class="d-flex justify-content-center mb-2">
          <button
            type="button"
            class="btn btn-secondary d-flex align-items-center me-2"
            tabindex="-1"
            [ngbTooltip]="'feedback.categoryGeneralTooltip' | translate"
            (click)="changeStep('general')"
          >
            <img src="/gematik-shared/assets/icons/star_blue.svg" />
            {{ 'feedback.categoryGeneral' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-secondary d-flex align-items-center ms-2"
            tabindex="-1"
            [ngbTooltip]="'feedback.inquiryTooltip' | translate"
            (click)="changeStep('inquiry')"
          >
            <img src="/gematik-shared/assets/icons/contact_support.svg" />
            {{ 'feedback.submitInquiry' | translate }}
          </button>
        </div>
      </div>
    </ng-container>

    <!-- General rating view (MyServices bewerten) -->
    <ng-container *ngIf="(currentStep$ | async) === 'general'">
      <div class="d-flex align-items-center position-relative p-3 mt-3 pb-0">
        <button mat-button class="position-absolute start-0 ms-2" tabindex="-1" [disabled]="isLoading" (click)="onBack()">
          <mat-icon>chevron_left</mat-icon>{{ 'feedback.back' | translate }}
        </button>
        <h3 class="text-center w-100 mb-0">{{ 'feedback.generalTitle' | translate }}</h3>
        <button
          mat-icon-button
          mat-dialog-close
          class="position-absolute end-0 me-2"
          tabindex="-1"
          [disabled]="isLoading"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <p class="text-center text-muted px-3 mb-0">{{ 'feedback.generalText' | translate }}</p>
      <div mat-dialog-content class="mat-dialog-content" style="color: initial">
        <gem-feedback-general-form
          [isLoading]="isLoading"
          (back)="onBack()"
          (feedback)="submit($event)"
        ></gem-feedback-general-form>
      </div>
    </ng-container>

    <!-- Chatbot view (embedded) -->
    <ng-container *ngIf="(currentStep$ | async) === 'chatbot'">
      <div class="d-flex align-items-center position-relative p-3 mt-3">
        <button mat-button class="position-absolute start-0 ms-2" tabindex="-1" (click)="onChatbotBack()">
          <mat-icon>chevron_left</mat-icon>{{ 'feedback.back' | translate }}
        </button>

        <h3 [innerHTML]="'feedback.chatbotViewTitle' | translate" class="text-center w-100 mb-0"></h3>

        <button
          mat-icon-button
          mat-dialog-close
          class="position-absolute end-0 me-2"
          tabindex="-1"
          (click)="onChatbotClose()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div mat-dialog-content class="mat-dialog-content" style="color: initial">
        <sc-chat-container class="embedded-chatbot" (closeChat)="onChatbotConfirmed()"></sc-chat-container>
      </div>
    </ng-container>

    <!-- Inquiry view with 3 tabs -->
    <ng-container *ngIf="(currentStep$ | async) === 'inquiry'">
      <div [hidden]="showAiAnswer">
        <div class="d-flex align-items-center position-relative p-3 mt-3">
          <button mat-button class="position-absolute start-0 ms-2" tabindex="-1" [disabled]="isLoading" (click)="onBack()">
            <mat-icon>chevron_left</mat-icon>{{ 'feedback.back' | translate }}
          </button>
          <h3 class="text-center w-100 mb-0">{{ 'feedback.inquiryTitle' | translate }}</h3>
          <button
            mat-icon-button
            mat-dialog-close
            class="position-absolute end-0 me-2"
            tabindex="-1"
            [disabled]="isLoading"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <mat-tab-group [(selectedIndex)]="selectedTabIndex" class="inquiry-tabs ms-4 me-5">
          <mat-tab [label]="'feedback.tabInquiry' | translate" labelClass="tab-inquiry"></mat-tab>
          <mat-tab [label]="'feedback.tabFeature' | translate" labelClass="tab-feature"></mat-tab>
          <mat-tab [label]="'feedback.tabIncident' | translate" labelClass="tab-incident"></mat-tab>
        </mat-tab-group>
        <div mat-dialog-content class="mat-dialog-content" style="color: initial">
          <gem-feedback-inquiry-form
            *ngIf="selectedTabIndex === 0"
            [isLoading]="isLoading"
            (back)="onBack()"
            (feedback)="submit($event)"
          ></gem-feedback-inquiry-form>
          <gem-feedback-feature-form
            *ngIf="selectedTabIndex === 1"
            [isLoading]="isLoading"
            (back)="onBack()"
            (feedback)="submit($event)"
          ></gem-feedback-feature-form>
          <gem-feedback-incident-form
            *ngIf="selectedTabIndex === 2"
            [isLoading]="isLoading"
            (back)="onBack()"
            (feedback)="submit($event)"
          ></gem-feedback-incident-form>
        </div>
      </div>

      <!-- AI pre-answer view (STE precheck) -->
      <div *ngIf="showAiAnswer" class="ai-answer-view">
        <div class="d-flex align-items-center gap-2 mt-3 mb-3 px-3">
          <div class="ai-answer-badge">{{ 'feedback.aiAnswerBadge' | translate }}</div>
          <div>
            <h3 class="mb-0" style="color: var(--gematik-blue)">{{ 'feedback.aiAnswerTitle' | translate }}</h3>
            <p class="text-muted mb-0">{{ 'feedback.aiAnswerSubTitle' | translate }}</p>
          </div>
        </div>

        <div mat-dialog-content class="mat-dialog-content ai-answer-scroll" style="color: initial">
          <div class="ai-answer-recap mx-3 mb-3 p-3">
            <div class="ai-answer-recap-label">{{ 'feedback.aiAnswerRecapLabel' | translate }}</div>
            <div class="fw-bold ai-answer-recap-title" [class.expanded]="recapExpanded">{{ recapTitle }}</div>
            <div class="ai-answer-recap-comments" [class.expanded]="recapExpanded">{{ recapComments }}</div>
            <button
              *ngIf="recapIsLong"
              type="button"
              class="ai-answer-recap-toggle"
              (click)="recapExpanded = !recapExpanded"
            >
              {{ (recapExpanded ? 'feedback.aiAnswerRecapCollapse' : 'feedback.aiAnswerRecapExpand') | translate }}
            </button>
          </div>

          <div class="ai-answer-text mx-3 mb-4" [innerHTML]="aiAnswerText | markdown"></div>
        </div>

        <div class="text-center border-top pt-4 px-3 pb-3">
          <p class="fw-bold mb-3">{{ 'feedback.aiAnswerQuestion' | translate }}</p>
          <div class="d-flex justify-content-center gap-3 flex-wrap">
            <button type="button" class="btn btn-secondary" [disabled]="isLoading" (click)="onAiAnswerNo()">
              {{ 'feedback.aiAnswerNo' | translate }}
            </button>
            <button type="button" class="btn btn-primary" [disabled]="isLoading" cdkFocusInitial (click)="onAiAnswerYes()">
              {{ 'feedback.aiAnswerYes' | translate }}
            </button>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  standalone: false,
})
export class GemFeedbackComponent implements OnInit, OnDestroy {
  @ViewChild(ChatContainerComponent) chatbotRef?: ChatContainerComponent;

  public subscription?: Subscription;
  public currentStep$: Observable<Step>;
  public chatAvailability: AiFeatureAvailability = 'DISABLED';
  public chatDisabledUntil: string | null = null;
  public steAvailability: AiFeatureAvailability = 'DISABLED';
  public selectedTabIndex = 0;

  /** Visible: feature is either usable now or temporarily in maintenance (shown disabled). */
  get canAccessChatbot(): boolean {
    return this.chatAvailability !== 'DISABLED';
  }

  /** Actually clickable/usable right now. */
  get chatInteractive(): boolean {
    return this.chatAvailability === 'ACTIVE';
  }

  get steInteractive(): boolean {
    return this.steAvailability === 'ACTIVE';
  }

  public chatbotSuggestions: string[] = [];

  private pendingAction: 'back' | 'close' = 'back';
  private readonly currentStepSubject = new BehaviorSubject<Step>('home');

  isLoading: boolean = false;

  private readonly DISABLE_SERVICE_REQUEST_SUBMIT_FOR_TESTING = false;

  showAiAnswer = false;
  aiAnswerText = '';
  aiConfidence = 0;
  recapTitle = '';
  recapComments = '';
  recapExpanded = false;
  private pendingFeedback?: Feedback;
  private pendingDocuments?: any[];
  private aiLogId?: number;

  get recapIsLong(): boolean {
    return this.recapTitle.length > 50 || this.recapComments.length > 100;
  }

  constructor(
    public dialogRef: MatDialogRef<GemFeedbackComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GemFeedbackData,
    public translateService: TranslateService,
    private readonly deviceService: DeviceDetectorService,
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    @Optional()
    @Inject(GEM_FEEDBACK_CHATBOT_LAUNCHER)
    private readonly chatbotLauncher: GemFeedbackChatbotLauncher | null,
    private readonly tokenHelperService: TokenHelperService,
    private readonly chatService: ChatService,
    private readonly steService: SteService,
    private readonly aiFeatureGate: AiFeatureGateService,
  ) {
    this.currentStep$ = this.currentStepSubject.asObservable();
  }

  ngOnInit(): void {
    this.subscription = this.aiFeatureGate.features$.subscribe(() => {
      const chat = this.aiFeatureGate.chatFeature;
      const ste = this.aiFeatureGate.steFeature;
      const hasToken = this.hasValidToken();

      this.chatAvailability = chat.authRequired && !hasToken ? 'DISABLED' : chat.availability;
      this.chatDisabledUntil = chat.disabledUntil;
      this.steAvailability = ste.authRequired && !hasToken ? 'DISABLED' : ste.availability;
    });
    this.chatbotSuggestions = [
      this.translateService.instant('feedback.chatbotSuggestion1'),
      this.translateService.instant('feedback.chatbotSuggestion2'),
      this.translateService.instant('feedback.chatbotSuggestion3'),
      this.translateService.instant('feedback.chatbotSuggestion4'),
      this.translateService.instant('feedback.chatbotSuggestion5'),
    ];
  }

  changeStep(step: Step) {
    if (step === 'chatbot' && !this.chatInteractive) {
      return;
    }
    this.currentStepSubject.next(step);
  }

  openChatbotWithQuestion(question: string) {
    if (!this.chatInteractive) {
      return;
    }
    this.chatService.resetConversation();
    this.currentStepSubject.next('chatbot');
    setTimeout(() => this.chatService.sendMessage(question));
  }

  onBack(): void {
    this.currentStepSubject.next('home');
  }

  onChatbotConfirmed(): void {
    if (this.pendingAction === 'close') {
      this.dialogRef.close();
    } else {
      this.onBack();
    }
    this.pendingAction = 'back';
  }

  onChatbotBack(): void {
    if (this.chatbotRef?.requiresCloseConfirmation) {
      this.chatbotRef.close();
    } else {
      this.onBack();
    }
  }

  onChatbotClose(): void {
    if (this.chatbotRef?.requiresCloseConfirmation) {
      this.pendingAction = 'close';
      this.chatbotRef.close();
    } else {
      this.dialogRef.close();
    }
  }

  submit(event: { feedback: Feedback; documents: any[] }) {
    const { feedback, documents } = event;

    // Produktentscheidung: Anonyme (nicht eingeloggte) User bekommen keine KI-Vorabantwort — sie erhalten
    // ohnehin eine E-Mail mit der Aufforderung, sich zu registrieren und ihre Anfrage nach Login neu zu stellen.
    if (AI_PRECHECK_CATEGORIES.includes(feedback.category) && this.steInteractive && this.hasValidToken()) {
      this.precheckWithAi(feedback, documents);
      return;
    }

    this.submitServiceRequestOrFeedback(feedback, documents);
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    // Prevent the user from closing the dialog (Escape / backdrop click) while a request is in flight;
    // the explicit close/back buttons are additionally disabled via [disabled]="isLoading" in the template.
    this.dialogRef.disableClose = loading;
  }

  private precheckWithAi(feedback: Feedback, documents: any[]): void {
    this.setLoading(true);
    const query = `${feedback.title || ''}\n${feedback.comments || ''}`.trim();

    // pending-config-ai-flag: Timeout ist hart codiert, bis das Admin-Modul die KI-Vorabantwort konfigurierbar macht.
    this.steService
      .submitSupportRequest(query)
      .pipe(
        timeout(20000),
        catchError(() => of({ answered: false, confidence: 0, answer: '', logId: 0 })),
      )
      .subscribe(response => {
        this.setLoading(false);

        if (response.answered) {
          this.pendingFeedback = feedback;
          this.pendingDocuments = documents;
          this.recapTitle = feedback.title || '';
          this.recapComments = feedback.comments || '';
          this.recapExpanded = false;
          this.aiAnswerText = response.answer;
          this.aiConfidence = response.confidence;
          this.aiLogId = response.logId;
          this.showAiAnswer = true;
        } else {
          this.submitServiceRequestOrFeedback(feedback, documents, response.logId);
        }
      });
  }

  /**
   * Folgeaktionen (z. B. Ticketerstellung) laufen nur, wenn ste/feedback erfolgreich war.
   * Nach Erfolg wird die logId verbraucht, damit das Feedback nie doppelt gesendet wird.
   */
  private submitAiFeedback(helpful: boolean, onSuccess: () => void): void {
    if (this.aiLogId === undefined) {
      return;
    }
    this.setLoading(true);
    this.steService.submitFeedback(this.aiLogId, helpful).subscribe({
      next: () => {
        this.aiLogId = undefined;
        this.setLoading(false);
        this.showAiAnswer = false;
        onSuccess();
      },
      error: (error: HttpErrorResponse) => {
        this.setLoading(false);
        if (error.status === 409) {
          // Feedback-Zeitfenster abgelaufen – ein erneuter Versuch ist zwecklos.
          this.showAiAnswer = false;
          this.dialogRef.close();
          this.dialog.open(GemErrorDialogComponent, {
            data: {
              title: this.translateService.instant('feedback.errorTitle'),
              errors: [this.translateService.instant('feedback.aiFeedbackExpiredText')],
            },
          });
          return;
        }
        // Feedback-Dialog bleibt offen, damit die Eingaben des Nutzers erhalten bleiben.
        this.dialog.open(GemConfirmationDialogComponent, {
          data: {
            title: this.translateService.instant('feedback.errorTitle'),
            text: this.translateService.instant('feedback.aiFeedbackErrorText', { status: error.status }),
            confirmText: this.translateService.instant('feedback.aiFeedbackRetry'),
            confirmAction: () => this.submitAiFeedback(helpful, onSuccess),
            cancelAction: () => {},
          },
        });
      },
    });
  }

  onAiAnswerYes(): void {
    this.submitAiFeedback(true, () => {
      this.dialogRef.close();
      this.dialog.open(GemDialogComponent, {
        data: {
          title: this.translateService.instant('feedback.aiAnswerHelpfulTitle'),
          text: this.translateService.instant('feedback.aiAnswerHelpfulText'),
          style: 'text-align: center',
        },
      });
    });
  }

  onAiAnswerNo(): void {
    const logId = this.aiLogId;
    this.submitAiFeedback(false, () => {
      if (this.pendingFeedback) {
        this.submitServiceRequestOrFeedback(this.pendingFeedback, this.pendingDocuments || [], logId);
      }
    });
  }

  private submitServiceRequestOrFeedback(feedback: Feedback, documents: any[], steLogId?: number) {
    this.setLoading(true);

    const metadata = {
      ...this.deviceService.getDeviceInfo(),
      isMobile: this.deviceService.isMobile(),
      isTablet: this.deviceService.isTablet(),
      isDesktop: this.deviceService.isDesktop(),
    };

    const isServiceRequest = ['INQUIRY', 'FEATURE', 'INCIDENT'].includes(feedback.category);

    const payload = isServiceRequest
      ? {
          requestType: feedback.category,
          title: feedback.title || '',
          department: feedback.department || '',
          description: feedback.comments,
          email: feedback.contact === 'WITH_EMAIL' ? feedback.email : '',
          application: this.data.application,
          component: feedback.department
            ? (this.translateService.translations?.['en']?.['feedback']?.[feedback.department] ??
              this.translateService.instant(`feedback.${feedback.department}`))
            : '',
          metadata: JSON.stringify(metadata),
          ste_confidence: this.aiConfidence,
          ste_answer: this.aiAnswerText || 'Keine - Außerhalb der KI-Wissensbasis',
          ste_log_id: steLogId,
        }
      : {
          rating: feedback.rating,
          email: feedback.contact === 'WITH_EMAIL' ? feedback.email : '',
          comment: feedback.comments,
          application: this.data.application,
          component: this.data.component,
          metadata: JSON.stringify(metadata),
          categoryName: feedback.category,
        };

    let successTitle = '';
    let successText = '';

    if (feedback.category === 'GENERAL') {
      successTitle = this.translateService.instant('feedback.generalSuccessTitle');
      successText = this.translateService.instant('feedback.generalSuccessText');
    } else if (feedback.category === 'FEATURE') {
      successTitle = this.translateService.instant('feedback.featureSuccessTitle');
      successText = this.translateService.instant('feedback.featureSuccessText');
    } else if (feedback.category === 'INCIDENT') {
      successTitle = this.translateService.instant('feedback.incidentSuccessTitle');
      successText = this.translateService.instant('feedback.incidentSuccessText');
    } else if (feedback.category === 'INQUIRY') {
      successTitle = this.translateService.instant('feedback.inquirySuccessTitle');
      successText = this.translateService.instant('feedback.inquirySuccessText');
    }

    const data = new FormData();
    data.append(
      'payload',
      new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      }),
    );

    if (documents) {
      documents.forEach(document => {
        data.append('files', document.file);
      });
    }

    const url = isServiceRequest
      ? `${this.data.backendUrl}/public/service-requests`
      : `${this.data.backendUrl}/public/feedback`;

    const skipRealSubmit = isServiceRequest && this.DISABLE_SERVICE_REQUEST_SUBMIT_FOR_TESTING;
    if (skipRealSubmit) {
      console.warn('[gem-feedback] Service-Request-Submit ist zu Testzwecken deaktiviert, es wurde kein echtes Ticket angelegt.', payload);
    }
    const submit$ = skipRealSubmit ? of(null) : this.http.post(url, data);

    this.subscription = submit$.subscribe({
      next: () => {
        this.dialogRef.close();
        this.dialog.open(GemDialogComponent, {
          data: {
            title: successTitle,
            text: successText,
            style: 'text-align: center',
          },
        });
      },
      error: (error: HttpErrorResponse) => {
        this.setLoading(false);
        // Dialog bleibt offen, damit die Eingaben erhalten bleiben. Der Retry sendet nur die Einreichung
        // erneut – ein ggf. bereits übermitteltes STE-Feedback wird nicht wiederholt.
        this.dialog.open(GemConfirmationDialogComponent, {
          data: {
            title: this.translateService.instant('feedback.errorTitle'),
            text: this.translateService.instant('feedback.submitErrorRetryText', { status: error.status }),
            confirmText: this.translateService.instant('feedback.aiFeedbackRetry'),
            confirmAction: () => this.submitServiceRequestOrFeedback(feedback, documents, steLogId),
            cancelAction: () => {},
          },
        });
      },
      complete: () => {
        this.setLoading(false);
      },
    });
  }

  async openChatbot(): Promise<void> {
    try {
      if (this.chatbotLauncher) {
        await this.chatbotLauncher();
        return;
      }

      this.dialog.open(GemFeedbackChatbotDialogComponent, {
        autoFocus: false,
        restoreFocus: false,
      });
    } catch {
      this.dialog.open(GemFeedbackChatbotDialogComponent, {
        autoFocus: false,
        restoreFocus: false,
      });
    }
  }

  formatUntil(disabledUntil: string | null): string {
    if (!disabledUntil) {
      return '';
    }
    const date = new Date(disabledUntil);
    return isNaN(date.getTime())
      ? disabledUntil
      : date.toLocaleString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  private hasValidToken(): boolean {
    try {
      const token = this.tokenHelperService.getToken();
      return !!token && !this.tokenHelperService.isExpired();
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
